// src/components/inventory/InventoryHistoryTab.tsx
// Cos'è successo al magazzino, dal più recente: ingressi, consumi, vendite.
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PackagePlus, Scissors, ShoppingBag } from 'lucide-react';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';

import { useInventory } from '@/contexts/InventoryContext';
import { MovementType } from '@/services/inventory/inventoryService';

type Filter = 'all' | MovementType;

const InventoryHistoryTab: React.FC = () => {
    const { t } = useTranslation();
    const { movements, getProductById } = useInventory();
    const [filter, setFilter] = useState<Filter>('all');

    const filters: { id: Filter; label: string }[] = [
        { id: 'all', label: t('inventory.history.all', 'Tutti') },
        { id: 'in', label: t('inventory.history.in', 'Ingressi') },
        { id: 'use', label: t('inventory.history.use', 'Usati') },
        { id: 'sale', label: t('inventory.history.sale', 'Venduti') },
    ];

    const rows = useMemo(
        () => (filter === 'all' ? movements : movements.filter((m) => m.type === filter)),
        [movements, filter]
    );

    const typeLabel = (type: MovementType) =>
        type === 'in'
            ? t('inventory.history.type_in', 'Ingresso')
            : type === 'use'
                ? t('inventory.history.type_use', 'Usato')
                : t('inventory.history.type_sale', 'Venduto');

    const typeIcon = (type: MovementType) => {
        if (type === 'in') return <PackagePlus size={16} className="text-clip-500" />;
        if (type === 'use') return <Scissors size={16} className="text-clip-500" />;
        return <ShoppingBag size={16} className="text-emerald-600" />;
    };

    return (
        <div className="space-y-3">
            <div className="bg-white rounded-2xl p-1.5 border border-gray-100 shadow-sm inline-flex gap-1">
                {filters.map((f) => (
                    <button
                        key={f.id}
                        type="button"
                        onClick={() => setFilter(f.id)}
                        className={`px-4 py-2 rounded-xl text-sm font-bold transition-all ${filter === f.id
                            ? 'bg-clip-500 text-white shadow-sm'
                            : 'text-gray-500 hover:bg-gray-50 hover:text-gray-900'
                            }`}
                    >
                        {f.label}
                    </button>
                ))}
            </div>

            <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
                {rows.length === 0 ? (
                    <p className="p-8 text-center text-sm text-gray-400">
                        {t('inventory.history.empty', 'Nessun movimento registrato.')}
                    </p>
                ) : (
                    rows.map((m, index) => {
                        const product = getProductById(m.product_id);
                        return (
                            <div
                                key={m.id}
                                className={`flex items-center gap-3 px-5 py-3.5 ${index > 0 ? 'border-t border-gray-100' : ''
                                    }`}
                            >
                                <div className="w-8 flex-shrink-0 flex items-center justify-center">
                                    {typeIcon(m.type)}
                                </div>
                                <div className="min-w-0 flex-1">
                                    <p className="text-sm text-gray-900">
                                        <span className="font-bold">
                                            {product?.name || t('inventory.history.deleted_product', 'Prodotto rimosso')}
                                        </span>{' '}
                                        <span className="text-gray-500">×{m.quantity}</span>
                                    </p>
                                    <p className="text-xs text-gray-400">
                                        {typeLabel(m.type)} ·{' '}
                                        {format(new Date(m.created_at), 'd MMM, HH:mm', { locale: it })}
                                    </p>
                                </div>
                                {m.type === 'sale' && m.total_amount != null && (
                                    <span className="flex-shrink-0 text-sm font-bold text-emerald-700 tabular-nums">
                                        €{Number(m.total_amount).toFixed(2)}
                                    </span>
                                )}
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
};

export default InventoryHistoryTab;
